// src/components/leitner/LeitnerVoiceGuide.jsx
import React, { useState, useRef, useEffect } from 'react';
import { 
  Volume2, Play, Pause, X, Plus, Trash2, 
  RotateCcw, HelpCircle, Loader
} from 'lucide-react';
import { VoiceRecorder } from '../shared/VoiceRecorder';
import { leitnerService } from '../../services/leitnerService';

// Format duration helper
const formatDuration = (seconds) => {
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
};

// Voice Player Modal
// Voice Player Modal - Replace the existing VoicePlayerModal
const VoicePlayerModal = ({ guide, onClose, styling }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [error, setError] = useState(null);
  const audioRef = useRef(null);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio || !guide?.url) return;

    const updateProgress = () => {
      if (audio.duration) {
        const percent = (audio.currentTime / audio.duration) * 100;
        setProgress(percent);
        setCurrentTime(audio.currentTime);
      }
    };

    const handleEnded = () => setIsPlaying(false);
    const handleError = (e) => {
      console.error('Audio error:', e);
      setError('Failed to load audio');
      setIsPlaying(false);
    };
    const handleCanPlay = () => {
      // Only auto-play after audio is ready
      audio.play()
        .then(() => setIsPlaying(true))
        .catch((err) => {
          console.log('Auto-play blocked:', err);
          setIsPlaying(false);
        });
    };

    audio.addEventListener('timeupdate', updateProgress);
    audio.addEventListener('ended', handleEnded);
    audio.addEventListener('error', handleError);
    audio.addEventListener('canplay', handleCanPlay);

    return () => {
      audio.removeEventListener('timeupdate', updateProgress);
      audio.removeEventListener('ended', handleEnded);
      audio.removeEventListener('error', handleError);
      audio.removeEventListener('canplay', handleCanPlay);
      audio.pause();
    };
  }, [guide?.url]);

  const togglePlay = () => {
    if (!audioRef.current || !guide?.url) return;
    
    if (isPlaying) {
      audioRef.current.pause();
      setIsPlaying(false);
    } else {
      audioRef.current.play()
        .then(() => setIsPlaying(true))
        .catch((err) => {
          console.error('Play failed:', err);
          setError('Failed to play audio');
        });
    }
  };

  const handleRestart = () => {
    if (!audioRef.current) return;
    audioRef.current.currentTime = 0;
    audioRef.current.play()
      .then(() => setIsPlaying(true))
      .catch(console.error);
  };

  const handleSeek = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const percent = x / rect.width;
    if (audioRef.current && audioRef.current.duration) {
      audioRef.current.currentTime = percent * audioRef.current.duration;
    }
  };

  // Early return if no valid URL
  if (!guide?.url) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm mx-4 p-6 text-center">
          <p className="text-red-500 mb-4">Audio not available</p>
          <button onClick={onClose} className="px-4 py-2 bg-gray-200 rounded-lg">Close</button>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm mx-4 overflow-hidden">
        <div 
          className="px-6 py-4 border-b"
          style={{ backgroundColor: styling?.primaryColor || '#6366f1' }}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-full bg-white/20 flex items-center justify-center ${isPlaying ? 'animate-pulse' : ''}`}>
                <Volume2 className="w-5 h-5 text-white" />
              </div>
              <div>
                <h3 className="font-semibold text-white truncate max-w-[180px]">{guide.title}</h3>
                <p className="text-xs text-white/70">{guide.speaker?.name || 'Guide'}</p>
              </div>
            </div>
            <button onClick={onClose} className="p-2 hover:bg-white/20 rounded-lg transition-colors">
              <X className="w-5 h-5 text-white" />
            </button>
          </div>
        </div>

        <div className="p-6 space-y-4">
          {error && (
            <div className="p-3 bg-red-50 text-red-600 text-sm rounded-lg text-center">
              {error}
            </div>
          )}

          <button
            onClick={togglePlay}
            disabled={!!error}
            className="w-full flex items-center justify-center gap-2 py-4 rounded-xl text-white font-medium transition-all hover:opacity-90 disabled:opacity-50"
            style={{ backgroundColor: styling?.primaryColor || '#6366f1' }}
          >
            {isPlaying ? <><Pause className="w-6 h-6" /> Pause</> : <><Play className="w-6 h-6" /> Play</>}
          </button>

          <div className="space-y-2">
            <div 
              className="h-2 bg-gray-200 rounded-full overflow-hidden cursor-pointer"
              onClick={handleSeek}
            >
              <div 
                className="h-full transition-all duration-300 rounded-full"
                style={{ width: `${progress}%`, backgroundColor: styling?.primaryColor || '#6366f1' }}
              />
            </div>
            <div className="flex justify-between items-center text-xs text-gray-500">
              <span>{formatDuration(currentTime)}</span>
              <button onClick={handleRestart} className="hover:text-indigo-600 transition-colors" title="Restart">
                <RotateCcw className="w-3 h-3" />
              </button>
              <span>{formatDuration(guide.duration || 0)}</span>
            </div>
          </div>
        </div>

        <audio ref={audioRef} src={guide.url} preload="auto" />
      </div>
    </div>
  );
};

// Main Component
export const LeitnerVoiceGuide = ({ bookId, isOwner, styling }) => {
  const [voiceGuides, setVoiceGuides] = useState([]);
  const [showPopup, setShowPopup] = useState(false);
  const [showRecorder, setShowRecorder] = useState(false);
  const [showPlayer, setShowPlayer] = useState(false);
  const [selectedGuide, setSelectedGuide] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadVoiceGuides();
  }, [bookId]);

  const loadVoiceGuides = async () => {
    try {
      setLoading(true);
      const guides = await leitnerService.getVoiceGuides(bookId);
      setVoiceGuides(guides);
    } catch (error) {
      console.error('Failed to load voice guides:', error);
    } finally {
      setLoading(false);
    }
  };

  // VoiceRecorder passes: { audioBlob, duration, speaker: { name, relation, isPinned } }
  const handleSaveVoice = async (voiceData) => {
    try {
        const newGuide = await leitnerService.addVoiceGuide(bookId, {
        audioBlob: voiceData.blob,  // Changed from voiceData.audioBlob
        duration: voiceData.duration,
        speaker: voiceData.speaker,
        title: voiceData.speaker?.name || 'Voice Guide'
        });
        setVoiceGuides(prev => [...prev, newGuide]);
        setShowRecorder(false);
    } catch (error) {
        console.error('Failed to save voice guide:', error);
        throw error;
    }
    };

  const handleDeleteGuide = async (guideId) => {
    if (!window.confirm('Delete this voice guide?')) return;
    try {
      await leitnerService.deleteVoiceGuide(bookId, guideId);
      setVoiceGuides(prev => prev.filter(g => g.id !== guideId));
    } catch (error) {
      console.error('Failed to delete guide:', error);
    }
  };

  const handleSelectGuide = (guide) => {
    setSelectedGuide(guide);
    setShowPopup(false);
    setShowPlayer(true);
  };

  if (loading) return null;
  if (voiceGuides.length === 0 && !isOwner) return null;

  return (
    <>
      {/* Trigger Button */}
      <button
        onClick={() => setShowPopup(true)}
        className="flex items-center gap-2 px-4 py-2 rounded-lg transition-all hover:opacity-90"
        style={{ 
          backgroundColor: `${styling?.primaryColor || '#6366f1'}15`,
          color: styling?.primaryColor || '#6366f1'
        }}
      >
        <HelpCircle className="w-4 h-4" />
        <span className="text-sm font-medium">How to Use</span>
        {voiceGuides.length > 0 && (
          <span 
            className="w-5 h-5 rounded-full text-white text-xs flex items-center justify-center"
            style={{ backgroundColor: styling?.primaryColor || '#6366f1' }}
          >
            {voiceGuides.length}
          </span>
        )}
      </button>

      {/* Guides List Popup */}
      {showPopup && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 overflow-hidden">
            <div 
              className="px-6 py-4 border-b flex items-center justify-between"
              style={{ backgroundColor: styling?.primaryColor || '#6366f1' }}
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
                  <HelpCircle className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h3 className="font-semibold text-white">Voice Guides</h3>
                  <p className="text-xs text-white/70">Learn how to use this system</p>
                </div>
              </div>
              <button onClick={() => setShowPopup(false)} className="p-2 hover:bg-white/20 rounded-lg transition-colors">
                <X className="w-5 h-5 text-white" />
              </button>
            </div>

            <div className="max-h-[60vh] overflow-y-auto">
              {voiceGuides.length === 0 ? (
                <div className="p-8 text-center text-gray-500">
                  <Volume2 className="w-12 h-12 mx-auto mb-3 opacity-30" />
                  <p>No voice guides available yet</p>
                </div>
              ) : (
                <div className="divide-y">
                  {voiceGuides.map((guide) => (
                    <div key={guide.id} className="px-6 py-4 flex items-center gap-4 hover:bg-gray-50 transition-colors">
                      <button onClick={() => handleSelectGuide(guide)} className="flex-1 flex items-center gap-4 text-left">
                        <div 
                          className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0"
                          style={{ backgroundColor: `${styling?.primaryColor || '#6366f1'}20` }}
                        >
                          <Volume2 className="w-5 h-5" style={{ color: styling?.primaryColor || '#6366f1' }} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h4 className="font-medium text-gray-900 truncate">{guide.title}</h4>
                          <p className="text-sm text-gray-500">{guide.speaker?.name || 'Guide'} • {formatDuration(guide.duration)}</p>
                        </div>
                      </button>
                      
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handleSelectGuide(guide)}
                          className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
                          style={{ color: styling?.primaryColor || '#6366f1' }}
                        >
                          <Play className="w-5 h-5" />
                        </button>
                        {isOwner && (
                          <button
                            onClick={() => handleDeleteGuide(guide.id)}
                            className="p-2 rounded-lg hover:bg-red-50 text-red-500 transition-colors"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {isOwner && (
              <div className="p-4 border-t bg-gray-50">
                <button
                  onClick={() => { setShowPopup(false); setShowRecorder(true); }}
                  className="w-full flex items-center justify-center gap-2 py-3 rounded-lg text-white font-medium transition-all hover:opacity-90"
                  style={{ backgroundColor: styling?.primaryColor || '#6366f1' }}
                >
                  <Plus className="w-5 h-5" />
                  Add Voice Guide
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Voice Recorder - It's already a modal, render directly */}
      {showRecorder && (
        <VoiceRecorder
          level="guide"
          onSave={handleSaveVoice}
          onCancel={() => setShowRecorder(false)}
        />
      )}

      {/* Voice Player */}
      {showPlayer && selectedGuide && (
        <VoicePlayerModal
          guide={selectedGuide}
          onClose={() => { setShowPlayer(false); setSelectedGuide(null); }}
          styling={styling}
        />
      )}
    </>
  );
};

export default LeitnerVoiceGuide;